// @flow

import React from 'react';
import * as Yup from 'yup';
import * as Actions from 'main-app/store/Actions';
import { i18n } from 'shared/utils';
import AddShipmentMutation from 'main-app/mutations/AddShipment';
import UpdateShipmentMutation from 'main-app/mutations/UpdateShipment';
import Divider from 'shared/components/common/Divider';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import styled from 'styled-components';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
  SelectOrderField,
  SelectCustomerField,
} from 'shared/components/form';

const ItemsPreview = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const ItemsPreviewTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: 13px;
`;

const ItemName = styled.span`
  color: #333;
`;

const ItemQty = styled.span`
  color: #666;
  margin-left: 12px;
  white-space: nowrap;
`;

const RemoveBtn = styled.span`
  color: #ff4d4f;
  cursor: pointer;
  margin-left: 12px;
  font-size: 12px;
  &:hover {
    text-decoration: underline;
  }
`;

const SerialInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 24px;
  font-size: 12px;
  color: #666;
`;

const SerialInput = styled.input`
  flex: 1;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  &:focus {
    outline: none;
    border-color: #1890ff;
  }
`;

const AddSerialBtn = styled.span`
  color: #1890ff;
  cursor: pointer;
  font-size: 12px;
  margin-left: 24px;
  &:hover {
    text-decoration: underline;
  }
`;

type Props = {
  onClose: () => void,
  onSuccess?: (shipment: Object) => void,
  onReopen?: () => void,
  createAnotherShipment?: boolean,
  shipment?: ?Object,
  defaultOrder?: ?Object,
};

const SHIPMENT_STATUS_OPTIONS = [
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Ready to Ship', value: 'READY' },
  { label: 'Shipped', value: 'SHIPPED' },
  { label: 'Delivered', value: 'DELIVERED' },
];

const AddUpdateShipmentModal = ({
  onClose,
  onSuccess,
  onReopen,
  createAnotherShipment,
  shipment,
  defaultOrder,
}: Props) => {
  const isEditing = !!shipment;

  const language = {
    title: isEditing ? i18n.t('Edit Shipment') : i18n.t('New Shipment'),
    submitButton: isEditing ? i18n.t('Save') : i18n.t('Create Shipment'),
  };

  return (
    <Modal isOpen onClose={onClose}>
      <Formik
        initialValues={{
          orderId: shipment
            ? shipment.order.id
            : defaultOrder
              ? { value: defaultOrder.id, label: defaultOrder.orderNumber }
              : '',
          customerId: shipment
            ? shipment.customer.id
            : defaultOrder && defaultOrder.customer
              ? { value: defaultOrder.customer.id, label: defaultOrder.customer.name }
              : '',
          status: shipment ? shipment.status : 'DRAFT',
          carrierName: shipment ? shipment.carrierName || '' : '',
          trackingNumber: shipment ? shipment.trackingNumber || '' : '',
          shippingMethod: shipment ? shipment.shippingMethod || '' : '',
          estimatedShipDate: shipment ? shipment.estimatedShipDate || null : null,
          estimatedDeliveryDate: shipment ? shipment.estimatedDeliveryDate || null : null,
          shippingAddressLine1: shipment
            ? shipment.shippingAddressLine1 || ''
            : (defaultOrder && (defaultOrder.shippingAddressLine1
              || (defaultOrder.customer && defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressLine1)
              || (defaultOrder.customer && defaultOrder.customer.shippingAddressLine1))) || '',
          shippingAddressLine2: shipment
            ? shipment.shippingAddressLine2 || ''
            : (defaultOrder && (defaultOrder.shippingAddressLine2
              || (defaultOrder.customer && defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressLine2)
              || (defaultOrder.customer && defaultOrder.customer.shippingAddressLine2))) || '',
          shippingAddressLine3: shipment
            ? shipment.shippingAddressLine3 || ''
            : (defaultOrder && (defaultOrder.shippingAddressLine3
              || (defaultOrder.customer && defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressLine3)
              || (defaultOrder.customer && defaultOrder.customer.shippingAddressLine3))) || '',
          shippingAddressCity: shipment
            ? shipment.shippingAddressCity || ''
            : (defaultOrder && (defaultOrder.shippingAddressCity
              || (defaultOrder.customer && defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressCity)
              || (defaultOrder.customer && defaultOrder.customer.shippingAddressCity))) || '',
          shippingAddressState: shipment
            ? shipment.shippingAddressState || ''
            : (defaultOrder && (defaultOrder.shippingAddressState
              || (defaultOrder.customer && defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressState)
              || (defaultOrder.customer && defaultOrder.customer.shippingAddressState))) || '',
          shippingAddressZip: shipment
            ? shipment.shippingAddressZip || ''
            : (defaultOrder && (defaultOrder.shippingAddressZip
              || (defaultOrder.customer && defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressZip)
              || (defaultOrder.customer && defaultOrder.customer.shippingAddressZip))) || '',
          notes: shipment ? shipment.notes || '' : '',
          items: (!shipment && defaultOrder && defaultOrder.yields && defaultOrder.yields.edges)
            ? defaultOrder.yields.edges.map(edge => ({
                itemId: edge.node.item.id,
                itemName: edge.node.item.name,
                quantity: edge.node.orderedQuantity,
                uomSymbol: (edge.node.item.quantityUOM && edge.node.item.quantityUOM.symbol) || '',
                serialNumbers: [],
              }))
            : [],
        }}
        validationSchema={Yup.object().shape({
          orderId: Yup.mixed().required(i18n.t('Order is required')),
          customerId: Yup.mixed().required(i18n.t('Customer is required')),
          status: Yup.string().required(i18n.t('Status is required')),
        })}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const orderIdVal =
            values.orderId && typeof values.orderId === 'object'
              ? values.orderId.value
              : values.orderId;
          const customerIdVal =
            values.customerId && typeof values.customerId === 'object'
              ? values.customerId.value
              : values.customerId;

          const statusVal =
            values.status && typeof values.status === 'object'
              ? values.status.value
              : values.status;

          const input = {
            orderId: orderIdVal,
            customerId: customerIdVal,
            status: statusVal,
            carrierName: values.carrierName || null,
            trackingNumber: values.trackingNumber || null,
            shippingMethod: values.shippingMethod || null,
            estimatedShipDate: values.estimatedShipDate || null,
            estimatedDeliveryDate: values.estimatedDeliveryDate || null,
            shippingAddressLine1: values.shippingAddressLine1 || null,
            shippingAddressLine2: values.shippingAddressLine2 || null,
            shippingAddressLine3: values.shippingAddressLine3 || null,
            shippingAddressCity: values.shippingAddressCity || null,
            shippingAddressState: values.shippingAddressState || null,
            shippingAddressZip: values.shippingAddressZip || null,
            notes: values.notes || null,
          };

          if (!isEditing && values.items && values.items.length > 0) {
            input.items = values.items.map(i => ({
              itemId: i.itemId,
              quantity: i.quantity,
              serialNumbers: (i.serialNumbers || []).filter(sn => sn && sn.trim()),
            }));
          }

          if (isEditing) {
            input.id = shipment.id;
          }

          const mutation = isEditing
            ? UpdateShipmentMutation
            : AddShipmentMutation;

          try {
            const response = await mutation.commit({
              variables: { input },
            });

            // Server-side validation errors are returned as a field (not thrown)
            // so they reach here as a resolved payload rather than a catch.
            console.log('[AddUpdateShipmentModal] mutation response:', JSON.stringify(response));
            if (isEditing && response.updateShipment && response.updateShipment.error) {
              console.log('[AddUpdateShipmentModal] blocking with error:', response.updateShipment.error);
              setSubmitting(false);
              Actions.alertNotification(response.updateShipment.error, 'error');
              return;
            }

            const newShipment = isEditing
              ? response.updateShipment.shipmentEdge.node
              : response.addShipment.shipmentEdge.node;

            if (values.createAnotherShipment && onReopen) {
              onReopen();
            } else {
              onClose();
              if (onSuccess) {
                onSuccess(newShipment);
              }
            }

            Actions.alertNotification(
              isEditing
                ? i18n.t('Shipment updated successfully')
                : i18n.t('Shipment created successfully'),
              'success',
            );
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'error');
          }
        }}
        render={({
          errors,
          values,
          isValid,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              <FieldGroupRow
                left={
                  <SelectOrderField
                    name="orderId"
                    label={i18n.t('Order')}
                    error={errors.orderId}
                    clearable
                    onChange={order => {
                      if (!order || !order.value) {
                        return;
                      }
                      const data = order.data;
                      if (data && data.customer) {
                        setFieldValue('customerId', {
                          value: data.customer.id,
                          label: data.customer.name,
                          data: data.customer,
                        });
                        // Auto-populate shipping address: order > customer default > legacy
                        const c = data.customer;
                        const da = c && c.defaultAddress;
                        setFieldValue(
                          'shippingAddressLine1',
                          data.shippingAddressLine1 || (da && da.addressLine1) || c.shippingAddressLine1 || ''
                        );
                        setFieldValue(
                          'shippingAddressLine2',
                          data.shippingAddressLine2 || (da && da.addressLine2) || c.shippingAddressLine2 || ''
                        );
                        setFieldValue(
                          'shippingAddressLine3',
                          data.shippingAddressLine3 || (da && da.addressLine3) || c.shippingAddressLine3 || ''
                        );
                        setFieldValue(
                          'shippingAddressCity',
                          data.shippingAddressCity || (da && da.addressCity) || c.shippingAddressCity || ''
                        );
                        setFieldValue(
                          'shippingAddressState',
                          data.shippingAddressState || (da && da.addressState) || c.shippingAddressState || ''
                        );
                        setFieldValue(
                          'shippingAddressZip',
                          data.shippingAddressZip || (da && da.addressZip) || c.shippingAddressZip || ''
                        );
                      }
                      // Auto-populate items from order yields
                      if (data && data.yields && data.yields.edges) {
                        const orderItems = data.yields.edges.map(edge => ({
                          itemId: edge.node.item.id,
                          itemName: edge.node.item.name,
                          quantity: edge.node.orderedQuantity,
                          uomSymbol: edge.node.item.quantityUOM?.symbol || '',
                          serialNumbers: [],
                        }));
                        setFieldValue('items', orderItems);
                      } else {
                        setFieldValue('items', []);
                      }
                    }}
                  />
                }
                right={
                  <SelectCustomerField
                    name="customerId"
                    error={errors.customerId}
                    onChange={customer => {
                      if (!customer || !customer.data) return;
                      const c = customer.data;
                      const da = c.defaultAddress;
                      setFieldValue('shippingAddressLine1', (da && da.addressLine1) || c.shippingAddressLine1 || '');
                      setFieldValue('shippingAddressLine2', (da && da.addressLine2) || c.shippingAddressLine2 || '');
                      setFieldValue('shippingAddressLine3', (da && da.addressLine3) || c.shippingAddressLine3 || '');
                      setFieldValue('shippingAddressCity', (da && da.addressCity) || c.shippingAddressCity || '');
                      setFieldValue('shippingAddressState', (da && da.addressState) || c.shippingAddressState || '');
                      setFieldValue('shippingAddressZip', (da && da.addressZip) || c.shippingAddressZip || '');
                    }}
                  />
                }
              />

              {!isEditing && values.items && values.items.length > 0 && (
                <ItemsPreview>
                  <ItemsPreviewTitle>
                    {i18n.t('Items from Order')} ({values.items.length})
                  </ItemsPreviewTitle>
                  {values.items.map((item, idx) => (
                    <div key={item.itemId}>
                      <ItemRow>
                        <ItemName>{item.itemName}</ItemName>
                        <div>
                          <ItemQty>
                            {item.quantity} {item.uomSymbol}
                          </ItemQty>
                          <RemoveBtn
                            onClick={() => {
                              const updated = values.items.filter((_, i) => i !== idx);
                              setFieldValue('items', updated);
                            }}
                          >
                            {i18n.t('Remove')}
                          </RemoveBtn>
                        </div>
                      </ItemRow>
                      {(item.serialNumbers || []).map((sn, snIdx) => (
                        <SerialInputRow key={snIdx}>
                          <span>{i18n.t('S/N')} {snIdx + 1}:</span>
                          <SerialInput
                            placeholder={`${i18n.t('Serial number')} ${snIdx + 1}`}
                            value={sn}
                            onChange={e => {
                              const updated = [...values.items];
                              const serials = [...(updated[idx].serialNumbers || [])];
                              serials[snIdx] = e.target.value;
                              updated[idx] = { ...updated[idx], serialNumbers: serials };
                              setFieldValue('items', updated);
                            }}
                          />
                          <RemoveBtn
                            onClick={() => {
                              const updated = [...values.items];
                              const serials = [...(updated[idx].serialNumbers || [])];
                              serials.splice(snIdx, 1);
                              updated[idx] = { ...updated[idx], serialNumbers: serials };
                              setFieldValue('items', updated);
                            }}
                          >
                            ×
                          </RemoveBtn>
                        </SerialInputRow>
                      ))}
                      <AddSerialBtn
                        onClick={() => {
                          const updated = [...values.items];
                          const serials = [...(updated[idx].serialNumbers || []), ''];
                          updated[idx] = { ...updated[idx], serialNumbers: serials };
                          setFieldValue('items', updated);
                        }}
                      >
                        + {i18n.t('Add Serial Number')}
                      </AddSerialBtn>
                    </div>
                  ))}
                </ItemsPreview>
              )}

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="status"
                    type="select"
                    label={i18n.t('Status')}
                    options={SHIPMENT_STATUS_OPTIONS}
                    error={errors.status}
                  />
                }
                right={
                  <FieldGroup
                    name="shippingMethod"
                    label={i18n.t('Shipping Method')}
                    placeholder={i18n.t('e.g. Ground, Express, Freight')}
                    error={errors.shippingMethod}
                  />
                }
              />

              <Divider style={{ marginTop: 8, marginBottom: 24 }} />

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="carrierName"
                    label={i18n.t('Carrier Name')}
                    placeholder={i18n.t('e.g. UPS, FedEx, USPS')}
                    error={errors.carrierName}
                  />
                }
                right={
                  <FieldGroup
                    name="trackingNumber"
                    label={i18n.t('Tracking Number')}
                    error={errors.trackingNumber}
                  />
                }
              />

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="estimatedShipDate"
                    type="date"
                    label={i18n.t('Estimated Ship Date')}
                    placeholder={i18n.t('Date')}
                    error={errors.estimatedShipDate}
                    allowPastDates
                  />
                }
                right={
                  <FieldGroup
                    name="estimatedDeliveryDate"
                    type="date"
                    label={i18n.t('Estimated Delivery Date')}
                    placeholder={i18n.t('Date')}
                    error={errors.estimatedDeliveryDate}
                    allowPastDates
                  />
                }
              />

              <Divider style={{ marginTop: 8, marginBottom: 24 }} />

              <AddressAutocomplete
                value={values.shippingAddressLine1 || ''}
                label={i18n.t('Address 1')}
                placeholder={i18n.t('Shipping Address Line 1')}
                error={errors.shippingAddressLine1}
                onChange={val => setFieldValue('shippingAddressLine1', val)}
                onAddressSelect={({ line1, city, state, zip }) => {
                  if (line1) setFieldValue('shippingAddressLine1', line1);
                  if (city) setFieldValue('shippingAddressCity', city);
                  if (state) setFieldValue('shippingAddressState', state);
                  if (zip) setFieldValue('shippingAddressZip', zip);
                }}
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="shippingAddressLine2"
                    label={i18n.t('Address 2')}
                    placeholder={i18n.t('Shipping Address Line 2')}
                    error={errors.shippingAddressLine2}
                  />
                }
                right={
                  <FieldGroup
                    name="shippingAddressLine3"
                    label={i18n.t('Address 3')}
                    placeholder={i18n.t('Shipping Address Line 3')}
                    error={errors.shippingAddressLine3}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="shippingAddressCity"
                    label={i18n.t('City')}
                    placeholder={i18n.t('City')}
                    error={errors.shippingAddressCity}
                  />
                }
                center={
                  <FieldGroup
                    name="shippingAddressState"
                    label={i18n.t('State')}
                    placeholder={i18n.t('State')}
                    error={errors.shippingAddressState}
                  />
                }
                right={
                  <FieldGroup
                    name="shippingAddressZip"
                    label={i18n.t('Postal Code')}
                    placeholder={i18n.t('Zip Code')}
                    error={errors.shippingAddressZip}
                  />
                }
              />

              <Divider style={{ marginTop: 8, marginBottom: 24 }} />

              <FieldGroup
                name="notes"
                type="textarea"
                label={i18n.t('Notes')}
                placeholder={i18n.t('Enter notes here...')}
                error={errors.notes}
              />

              {!isEditing && (
                <FieldGroup
                  label={i18n.t('Create Another Shipment')}
                  sideLabel="right"
                  name="createAnotherShipment"
                  type="toggle"
                />
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {language.submitButton}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

AddUpdateShipmentModal.defaultProps = {
  onSuccess: undefined,
  onReopen: undefined,
  createAnotherShipment: false,
  shipment: null,
  defaultOrder: null,
};

export default AddUpdateShipmentModal;
